import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Check } from 'lucide-react';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const CustomDropdown = ({
  options,
  value,
  onChange,
  placeholder = "Select an option",
  className,
  dropdownClassName,
  icon: Icon
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "w-full flex items-center justify-between text-left",
          "bg-slate-900/80 border border-slate-700/80 rounded-xl py-3 px-4 text-sm text-gray-200",
          "focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all",
          "hover:bg-slate-800/80 cursor-pointer shadow-sm",
          isOpen && "border-blue-500 ring-1 ring-blue-500",
          className
        )}
      >
        <div className="flex items-center gap-2 overflow-hidden w-full">
            {Icon && <Icon className="w-4 h-4 text-slate-400 shrink-0" />}
            <div className={cn("truncate w-full flex-1", !selectedOption && "text-slate-400")}>
                {selectedOption ? (selectedOption.plainLabel || selectedOption.label) : placeholder}
            </div>
        </div>
        <motion.div
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={{ duration: 0.2 }}
        >
            <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
        </motion.div>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -5, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -5, scale: 0.98 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className={cn(
              "absolute z-50 w-full mt-2 rounded-xl bg-slate-900/95 border border-slate-700/50",
              "shadow-xl shadow-black/50 backdrop-blur-md overflow-hidden",
              dropdownClassName
            )}
          >
            <div className="max-h-60 overflow-y-auto custom-scrollbar py-1">
              {options.length === 0 ? (
                 <div className="px-4 py-3 text-sm text-slate-400 text-center">
                    No options available
                 </div>
              ) : (
                  options.map((option) => {
                    const isSelected = option.value === value;
                    return (
                        <button
                        key={option.value}
                        type="button"
                        onClick={() => {
                            onChange(option.value);
                            setIsOpen(false);
                        }}
                        className={cn(
                            "w-full flex items-center justify-between px-4 py-3 md:py-2.5 text-sm text-left rounded-lg",
                            "transition-colors hover:bg-slate-800/80 cursor-pointer",
                            isSelected ? "text-blue-400 font-medium bg-blue-500/10" : "text-gray-300"
                        )}
                        >
                        <div className="truncate w-full pr-3">{option.label}</div>
                        {isSelected && <Check className="w-4 h-4 text-blue-500 shrink-0" />}
                        </button>
                    );
                  })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CustomDropdown;
